import { Box, Divider, Fade, Grid, List, ListItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { IActivityStep } from '../../../redux/activity/activitySlice';
import { slimScrollbarStyles } from '../../../utils/styles';

interface IMagicSuggestionCardProps {
  name: string;
  activitySteps: IActivityStep[];
  objective?: string;
  closeModal?: () => void;
}
function MagicSuggestionCard({
  name,
  activitySteps,
  objective,
  closeModal,
}: IMagicSuggestionCardProps) {
  const { t } = useTranslation();
  const isAdvancedTrackingOptionsExists = activitySteps
    ? activitySteps.some((step) => step.isAdvanceStep)
    : false;
  let normalStepCounter = 0;

  return (
    <Box
      maxHeight={closeModal ? '90vh' : '60vh'}
      width={closeModal ? '100%' : 363}
      gap="24px"
      px={2}
      my={3}
      py={closeModal === undefined ? 3 : 0}
      borderRadius="10px"
      bgcolor="#fff"
      sx={{
        overflowX: 'auto',
        ...slimScrollbarStyles,
      }}
    >
      <Grid container spacing={3} flexDirection="column">
        <Grid item gap="10px" display="flex" justifyContent="space-between" alignItems="start">
          <Typography variant="bodyBold" color="primary.main">
            {name}
          </Typography>
          {closeModal && (
            <img
              src="/assets/icons/close-circle-large.svg"
              alt="close-icon"
              onClick={closeModal}
              role="presentation"
              style={{
                cursor: 'pointer',
                maxHeight: '22px',
              }}
            />
          )}
        </Grid>
        {closeModal && (
          <Grid item>
            <Divider />
          </Grid>
        )}
        {objective && (
          <Grid item>
            <Box display="flex" flexDirection="column" gap="10px">
              <Typography variant="meta" color="primary.main" fontWeight={700}>
                {t('activity.magicSuggestion.objective')}
              </Typography>

              <Typography color="primary.main" variant="meta" component="span">
                {objective}
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid item>
          <Box display="flex" flexDirection="column" gap="10px">
            <List
              sx={{
                listStyleType: 'circle',
                paddingInlineStart: '20px',
                '& .MuiListItem-root': {
                  display: 'list-item',
                },
              }}
            >
              {activitySteps.map((step) => {
                if (!step.isAdvanceStep) {
                  normalStepCounter += 1;
                  return (
                    <Box>
                      <ListItem
                        component="li"
                        key={step.id}
                        style={{
                          listStyleType: 'disc',
                          paddingInlineStart: '0px',
                          padding: '0px',
                          width: 'max-content',
                        }}
                      >
                        {/* mentioned  as mixed style in figma */}
                        <Typography
                          fontFamily="satoshi"
                          component="span"
                          color="primary.main"
                          fontWeight="700"
                          variant="meta"
                        >
                          {`${t('activity.createActivity.step')} ${normalStepCounter}: `}
                        </Typography>
                        <Typography
                          fontFamily="satoshi"
                          color="primary.main"
                          fontWeight="400"
                          variant="meta"
                          component="span"
                        >
                          {step.text}
                        </Typography>
                      </ListItem>
                      <Box position="relative">
                        {step.asset && step.assetType === 'IMAGE' && (
                          <Fade
                            in={!!step.asset}
                            timeout={{
                              enter: 1000,
                              exit: 300,
                            }}
                          >
                            <img
                              src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${step.asset}`}
                              alt={`Step ${normalStepCounter}`}
                              style={{
                                width: '150px',
                                height: '150px',
                              }}
                            />
                          </Fade>
                        )}
                        {step.asset && step.assetType === 'VIDEO' && (
                          <ReactPlayer
                            url={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${step.asset}`}
                            width="150px"
                            height="150px"
                            controls
                            data-testid="demo-video-player"
                          />
                        )}
                        {step.asset && step.assetType === 'AUDIO' && (
                          <audio
                            src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${step.asset}`}
                            controls
                            style={{ width: '150px' }}
                          >
                            <track kind="captions" />
                          </audio>
                        )}
                      </Box>
                    </Box>
                  );
                }
                return null;
              })}
            </List>
          </Box>
        </Grid>
        {isAdvancedTrackingOptionsExists && (
          <Grid item>
            <Box display="flex" flexDirection="column" gap="10px">
              <Typography variant="meta" color="primary.main" fontWeight={700}>
                {t('activity.magicSuggestion.toCompleteActivity')}
              </Typography>
              <Box>
                <List
                  sx={{
                    listStyleType: 'circle',
                    paddingInlineStart: '20px',
                    py: 0,
                    '& .MuiListItem-root': {
                      display: 'list-item',
                    },
                  }}
                >
                  {activitySteps.map((step, _index) =>
                    step.isAdvanceStep ? (
                      <ListItem
                        component="li"
                        key={step.id}
                        style={{
                          listStyleType: 'disc',
                          paddingInlineStart: '0px',
                          padding: '0px',
                          width: 'max-content',
                        }}
                      >
                        <Typography
                          fontFamily="satoshi"
                          color="primary.main"
                          fontWeight="400"
                          variant="meta"
                          component="span"
                        >
                          {step.text?.replace(/\([^)]*\)/g, '').trim()}
                        </Typography>
                      </ListItem>
                    ) : null
                  )}
                </List>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default MagicSuggestionCard;
